import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Table from "mui-datatables";
import { MuiThemeProvider } from "@material-ui/core/styles";

import errorHandler from "../../../../../../../hoc/ErrorHandler";
import context from "../../../../../../../context/auth-context";
import {
  TO_JSON_SERIALIZABLE,
  DISMISS_MEGA_MENU,
  EBOARD_TABLE_THEME,
} from "../../../../../../../constants";
import SlidingPane from "../../../../../../UI/SlidingPane/SlidingPane";
import Spinner from "../../../../../../UI/Spinner/Spinner";
import HTTPError from "../../../../../../UI/HTTPError/MainPage";

const AuditLogs = () => {
  const [auditTypes, setAuditTypes] = useState(["API", "SMS"]);
  const [auditLogs, setAuditLogs] = useState({
    web: null,
    API: null,
    SMS: null,
  });
  const [auditPaneOpen, setAuditPaneOpen] = useState(false);
  const [selectedAuditType, setSelectedAuditType] = useState(null);
  const [loading, setLoading] = useState({
    types: true,
    logs: true,
  });
  const [error, setError] = useState({
    types: false,
    logs: false,
  });
  const [tableColumns, setTableColumns] = useState(null);
  const [tableData, setTableData] = useState(null);

  const authContext = useContext(context);
  const { accessToken, companyId, companyName, tokenIsValid } = authContext;

  useEffect(() => {
    if (tokenIsValid) {
      (async () => {
        const response = await axios.get("/utilities/audit-types");
        if (response) {
          setLoading((preloaders) => ({
            ...preloaders,
            types: false,
          }));
          setError((errors) => ({
            ...errors,
            types: false,
          }));
          if (response.data) {
            return setAuditTypes((types) => [...types, ...response.data]);
          }

          return;
        } else {
          setLoading((preloaders) => ({
            ...preloaders,
            types: false,
          }));
          return setError((errors) => ({
            ...errors,
            types: true,
          }));
        }
      })();
    }
    return () => {
      setAuditTypes(["API", "SMS"]);
      setError((errors) => ({
        ...errors,
        types: false,
      }));
    };
  }, [accessToken, companyId, tokenIsValid]);

  useEffect(() => {
    if (tokenIsValid) {
      if (selectedAuditType) {
        (async () => {
          const response = await axios.get(
            `/utilities/audit-logs/${TO_JSON_SERIALIZABLE(selectedAuditType)}`
          );
          if (response) {
            setLoading((preloaders) => ({
              ...preloaders,
              logs: false,
            }));
            setError((errors) => ({
              ...errors,
              logs: false,
            }));
            if (response.data) {
              switch (selectedAuditType) {
                case "API":
                  return setAuditLogs((logs) => ({
                    ...logs,
                    API: response.data,
                  }));
                case "SMS":
                  return setAuditLogs((logs) => ({
                    ...logs,
                    SMS: response.data,
                  }));
                default:
                  return setAuditLogs((logs) => ({
                    ...logs,
                    web: response.data,
                  }));
              }
            }

            return;
          } else {
            setLoading((preloaders) => ({
              ...preloaders,
              logs: false,
            }));
            return setError((errors) => ({
              ...errors,
              logs: true,
            }));
          }
        })();
      }
    }

    return () => {
      setAuditLogs({
        web: null,
        API: null,
        SMS: null,
      });
      setError((errors) => ({
        ...errors,
        logs: false,
      }));
    };
  }, [accessToken, companyId, selectedAuditType, tokenIsValid]);

  useEffect(() => {
    if (tokenIsValid) {
      if (selectedAuditType) {
        const { API, SMS, web } = auditLogs;
        let columns, data;

        switch (selectedAuditType) {
          case "API": {
            columns = [
              {
                name: "index",
                label: "Log #",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              "Date",
              "Time",
              "User",
              {
                name: "ip",
                label: "IP Address",
                options: {
                  sort: false,
                },
              },
              {
                name: "device",
                label: "Device",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              {
                name: "uri",
                label: "URI",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              {
                name: "response",
                label: "API response",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              "Method",
            ];
            data = API
              ? API.map((log) => [
                  log.id,
                  log.date,
                  log.time,
                  log.user,
                  log.ip_address,
                  log.device,
                  log.uri,
                  log.response,
                  log.method,
                ])
              : [];
            break;
          }
          case "SMS": {
            columns = [
              {
                name: "index",
                label: "Log #",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              "Date",
              "Time",
              "Initiator",
              "Type",
              "Status",
              {
                name: "message",
                label: "Message",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              {
                name: "recipient",
                label: "Recipient",
                options: {
                  sort: false,
                },
              },
              {
                name: "cost",
                label: "Cost",
                options: {
                  sort: false,
                },
              },
            ];
            data = SMS
              ? SMS.map((log) => [
                  log.id,
                  log.date,
                  log.time,
                  log.initiator,
                  log.type,
                  log.status,
                  log.message,
                  log.recipient,
                  log.cost,
                ])
              : [];
            break;
          }
          default: {
            columns = [
              {
                name: "index",
                label: "Log #",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              "Date",
              "Time",
              "User",
              {
                name: "action",
                label: "Action",
                options: {
                  sort: false,
                  filter: false,
                },
              },
              {
                name: "ip",
                label: "IP Address",
                options: {
                  sort: false,
                },
              },
              ...(["Upload", "Download", "Archive File"].indexOf(
                selectedAuditType
              ) >= 0
                ? [
                    {
                      name: "file_name",
                      label: "File name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(selectedAuditType === "Delete File"
                ? [
                    {
                      name: "file_name",
                      label: "File name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                    {
                      name: "folder_name",
                      label: "Folder name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(["Delete Folder", "Rename Folder", "Archive Folder"].indexOf(
                selectedAuditType
              ) >= 0
                ? [
                    {
                      name: "folder_name",
                      label: "Folder name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(selectedAuditType === "Rename File"
                ? [
                    {
                      name: "file_name",
                      label: "File name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
              ...(["Rename Folder", "Rename File"].indexOf(selectedAuditType) >=
              0
                ? [
                    {
                      name: "old_name",
                      label: "Old name",
                      options: {
                        sort: false,
                        filter: false,
                      },
                    },
                  ]
                : []),
            ];
            data = web
              ? web.map((log) => [
                  log.id,
                  log.date,
                  log.time,
                  log.user,
                  log.action,
                  log.ip_address,
                  ...(["Upload", "Download", "Archive File"].indexOf(
                    selectedAuditType
                  ) >= 0
                    ? [log.doc_name]
                    : []),
                  ...(selectedAuditType === "Delete File"
                    ? [log.doc_name, log.folder_name]
                    : []),
                  ...([
                    "Delete Folder",
                    "Rename Folder",
                    "Archive Folder",
                  ].indexOf(selectedAuditType) >= 0
                    ? [log.folder_name]
                    : []),
                  ...(selectedAuditType === "Rename File"
                    ? [log.doc_name]
                    : []),
                  ...(["Rename Folder", "Rename File"].indexOf(
                    selectedAuditType
                  ) >= 0
                    ? [log.prev_doc_name]
                    : []),
                ])
              : [];
          }
        }

        setTableColumns(columns);
        setTableData(data);
      }
    }
  }, [selectedAuditType, auditLogs, tokenIsValid]);

  const auditTypeClickHandler = (type) => {
    DISMISS_MEGA_MENU();
    setAuditPaneOpen(true);

    return setSelectedAuditType(type);
  };

  const auditPaneCloseHandler = () => {
    setAuditPaneOpen(false);
    setSelectedAuditType(null);
    setError((errors) => ({
      ...errors,
      logs: false,
    }));
    setLoading((preloaders) => ({
      ...preloaders,
      logs: true,
    }));

    return;
  };

  const tableOptions = {
    filter: true,
    viewColumns: true,
    selectableRows: "none",
    textLabels: {
      body: {
        noMatch: "No logs found",
        toolTip: "Sort by column",
      },
      toolbar: {
        print: "Print logs",
      },
    },
    sort: true,
    download: false,
    rowsPerPage: selectedAuditType === "API" ? 6 : 8,
    search: false,
  };

  return (
    <>
      <div className="col-md-3">
        <h5>Audit logs</h5>
        <ul className="blocks-2">
          {!loading.types ? (
            !error.types ? (
              auditTypes.map((type) => (
                <li key={type} className="mega-menu m-0">
                  <ul className="list-icons">
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={auditTypeClickHandler.bind(this, type)}
                      style={{
                        fontWeight: type === selectedAuditType ? 900 : "normal",
                      }}
                    >
                      <i className="md-chevron-right" aria-hidden="true"></i>
                      &nbsp;{type}
                    </button>
                  </ul>
                </li>
              ))
            ) : (
              <HTTPError />
            )
          ) : (
            <Spinner />
          )}
        </ul>
      </div>
      <SlidingPane
        open={auditPaneOpen}
        width="100%"
        title={`${selectedAuditType} Audit Trails`}
        subtitle={`${companyName}`}
        onRequestClose={auditPaneCloseHandler}
        closeIcon={
          <i
            className="icon ti-close animation-scale-up"
            style={{ fontWeight: 700 }}
            aria-hidden="true"
          ></i>
        }
      >
        {!loading.logs ? (
          !error.logs ? (
            tableData &&
            tableColumns && (
              <MuiThemeProvider theme={EBOARD_TABLE_THEME()}>
                <Table
                  title=""
                  data={tableData}
                  columns={tableColumns}
                  options={tableOptions}
                />
              </MuiThemeProvider>
            )
          ) : (
            <HTTPError />
          )
        ) : (
          <Spinner />
        )}
      </SlidingPane>
    </>
  );
};

export default errorHandler(AuditLogs, axios);
